import { getCurrentCurrency } from "@stores/currency/currencyStore";
import { getCurrentLanguage } from "@stores/language/languageStore";
import { localeOf, type CurrencyCode, type LanguageCode } from "@lib/i18n/languages";

/** Turns API role strings (e.g. `platform_staff`) into English labels (e.g. `Platform Staff`). */
export function formatRoleLabel(role: string | null | undefined, fallback = "Platform user"): string {
    if (role == null || !role.trim()) {
        return fallback;
    }
    return role
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}

export interface FormatContext {
    language?: LanguageCode;
    currency?: CurrencyCode;
}

/**
 * Backend money is a decimal string ("389.85") in PKR. The currency setting only changes how the
 * figure is labelled — it is not converted (same as the rider and customer apps).
 */
export function formatMoney(
    value: string | number | null | undefined,
    ctx: FormatContext = {}
): string {
    const n = Number(value);
    return new Intl.NumberFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        style: "currency",
        currency: ctx.currency ?? getCurrentCurrency(),
        maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
}

/** A missing or unparseable date shows a dash instead of "Invalid Date" or 1 January 1970. */
function toValidDate(date: string | Date | null | undefined): Date | null {
    if (date == null || date === "") return null;
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(date: string | Date | null | undefined, ctx: FormatContext = {}): string {
    const d = toValidDate(date);
    if (!d) return "—";
    return new Intl.DateTimeFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        year: "numeric",
        month: "short",
        day: "numeric",
    }).format(d);
}

export function formatDateTime(
    date: string | Date | null | undefined,
    ctx: FormatContext = {}
): string {
    const d = toValidDate(date);
    if (!d) return "—";
    return new Intl.DateTimeFormat(localeOf(ctx.language ?? getCurrentLanguage()), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(d);
}
